import { http } from '../client';
import type { CallOptions, WriteOptions } from '../types';
import type { IntegrationsApi } from './integrations';

// Paths mirror docs/integration-settings-api.md (subsystems/platform/facade/adapters/admin-api/src/admin/integrations.rs and
// provider_settings.rs), relative to /admin/v1. Every write sends an Idempotency-Key (client.ts
// generates one unless the caller passes the key of the operation it is retrying).
const seg = encodeURIComponent;
const rd = (o?: CallOptions) => ({ signal: o?.signal });
const wr = (o?: WriteOptions) => ({ signal: o?.signal, idempotencyKey: o?.idempotencyKey, notifyCustomer: o?.notifyCustomer });

// One configuration per connection / provider (ADR-018): no path names an environment.
const conn = (id: string) => `/integrations/connections/${seg(id)}`;
const provider = (p: string) => `/provider-settings/${seg(p)}`;
const SPONSORS = '/provider-settings/VNNIC_GTLD_REPORTING/sponsoring-registrars';

export const integrationsHttp: IntegrationsApi = {
  // §2 Context
  getIntegrationContext: (opts) => http.get('api', '/integrations/context', rd(opts)),

  // §3 Connections
  listConnections: (q, opts) =>
    http.get('api', '/integrations/connections', { query: { kind: q.kind, protocol: q.protocol, status: q.status, q: q.q }, ...rd(opts) }),
  createConnection: (body, opts) => http.post('api', '/integrations/connections', body, wr(opts)),
  getConnection: (id, opts) => http.get('api', conn(id), rd(opts)),
  updateConnection: (id, body, opts) => http.patch('api', conn(id), body, wr(opts)),
  setConnectionRole: (id, body, opts) => http.put('api', `${conn(id)}/role`, body, wr(opts)),

  // §4 Settings, credentials, certificates, tests
  getConnectionEnvironment: (id, opts) => http.get('api', `${conn(id)}/settings`, rd(opts)),
  listEnvironmentVersions: (id, q, opts) =>
    http.get('api', `${conn(id)}/settings/versions`, { query: { before_version: q.before_version, limit: q.limit }, ...rd(opts) }),
  proposeEnvironmentVersion: (id, body, opts) => http.post('api', `${conn(id)}/settings/versions`, body, wr(opts)),
  listCredentialVersions: (id, opts) => http.get('api', `${conn(id)}/credentials`, rd(opts)),
  proposeCredential: (id, body, opts) => http.post('api', `${conn(id)}/credentials`, body, wr(opts)),
  revokeCredential: (id, version, body, opts) =>
    http.post('api', `${conn(id)}/credentials/${seg(String(version))}/revoke`, body, wr(opts)),
  listCertificateVersions: (id, opts) => http.get('api', `${conn(id)}/certificates`, rd(opts)),
  proposeCertificate: (id, body, opts) => http.post('api', `${conn(id)}/certificates`, body, wr(opts)),
  importCertificatePkcs12: (id, body, opts) => http.post('api', `${conn(id)}/certificates/pkcs12`, body, wr(opts)),
  revokeCertificate: (id, version, body, opts) =>
    http.post('api', `${conn(id)}/certificates/${seg(String(version))}/revoke`, body, wr(opts)),
  testConnection: (id, body, opts) => http.post('api', `${conn(id)}/test`, body, wr(opts)),
  getAccountBalance: (id, opts) => http.get('api', `${conn(id)}/balance`, rd(opts)),
  getAccountDomains: (id, q, opts) =>
    http.get('api', `${conn(id)}/domains`, { query: { page: q.page, limit: q.limit, filter: q.filter, q: q.q, refresh: q.refresh }, ...rd(opts) }),
  previewRegistryDomain: (id, name, opts) => http.get('api', `${conn(id)}/domains/${seg(name)}/registry`, rd(opts)),
  importRegistryDomain: (id, name, body, opts) => http.post('api', `${conn(id)}/domains/${seg(name)}/import`, body, wr(opts)),
  startImportAll: (id, body, opts) => http.post('api', `${conn(id)}/domains/import-all`, body, wr(opts)),
  getImportAll: (id, opts) => http.get('api', `${conn(id)}/domains/import-all`, rd(opts)),
  startOteExercise: (id, body, opts) => http.post('api', `${conn(id)}/exercise`, body, wr(opts)),
  listOteExercises: (id, q, opts) => http.get('api', `${conn(id)}/exercises`, { query: { limit: q.limit }, ...rd(opts) }),
  getOteExercise: (id, runId, opts) => http.get('api', `${conn(id)}/exercises/${seg(runId)}`, rd(opts)),

  // §5 TLD channels
  listTldChannels: (q, opts) =>
    http.get('api', '/integrations/tld-channels', {
      query: {
        channel: q.channel,
        connection_id: q.connection_id,
        source: q.source,
        tld_class: q.tld_class,
        q: q.q,
        tld_id: q.tld_id,
        after: q.after,
        limit: q.limit,
      },
      ...rd(opts),
    }),
  getTldChannel: (tldId, opts) => http.get('api', `/integrations/tld-channels/${seg(tldId)}`, rd(opts)),
  proposeTldChannel: (tldId, body, opts) => http.post('api', `/integrations/tld-channels/${seg(tldId)}`, body, wr(opts)),
  proposeTldChannelBatch: (body, opts) => http.post('api', '/integrations/tld-channels/batch', body, wr(opts)),

  // §6 Provider settings
  getProviderCatalog: (opts) => http.get('api', '/provider-settings/catalog', rd(opts)),
  listProviderSettings: (opts) => http.get('api', '/provider-settings', rd(opts)),
  getProviderSetting: (p, opts) => http.get('api', provider(p), rd(opts)),
  listProviderSettingVersions: (p, q, opts) =>
    http.get('api', `${provider(p)}/versions`, { query: { before_version: q.before_version, limit: q.limit }, ...rd(opts) }),
  proposeProviderSetting: (p, body, opts) => http.post('api', `${provider(p)}/versions`, body, wr(opts)),
  testProviderSetting: (p, body, opts) => http.post('api', `${provider(p)}/test`, body, wr(opts)),
  listSponsoringRegistrars: (opts) => http.get('api', SPONSORS, rd(opts)),
  proposeSponsoringRegistrar: (code, body, opts) => http.put('api', `${SPONSORS}/${seg(code)}`, body, wr(opts)),

  // §7 Stored secrets
  destroyStoredSecret: (id, body, opts) => http.post('api', `/integrations/stored-secrets/${seg(id)}/destroy`, body, wr(opts)),

  // §8 Changes waiting for approval (the decision goes through the existing approvals endpoint)
  listIntegrationChanges: (q, opts) =>
    http.get('api', '/integrations/changes', {
      query: { state: q.state, kind: q.kind, before: q.before, limit: q.limit },
      ...rd(opts),
    }),
  getIntegrationChange: (approvalId, opts) => http.get('api', `/integrations/changes/${seg(approvalId)}`, rd(opts)),
  decideIntegrationChange: (approvalId, body, opts) => http.post('api', `/approvals/${seg(approvalId)}/decision`, body, wr(opts)),
  applyIntegrationChange: (approvalId, body, opts) => http.post('api', `/integrations/changes/${seg(approvalId)}/apply`, body, wr(opts)),
  cancelIntegrationChange: (approvalId, body, opts) => http.post('api', `/integrations/changes/${seg(approvalId)}/cancel`, body, wr(opts)),

  // §9 Tests
  listConnectionTests: (q, opts) =>
    http.get('api', '/integrations/connection-tests', {
      query: { connection_id: q.connection_id, provider: q.provider, before: q.before, limit: q.limit },
      ...rd(opts),
    }),

  // §10 History
  listIntegrationHistory: (q, opts) =>
    http.get('api', '/integrations/history', {
      query: {
        resource_type: q.resource_type,
        resource_id: q.resource_id,
        environment: q.environment,
        action: q.action,
        actor_id: q.actor_id,
        before: q.before,
        limit: q.limit,
      },
      ...rd(opts),
    }),
};
